/**
 * One boundary per Overview slot, so a widget that throws while rendering
 * blanks its own panel and nothing else.
 *
 * Without it a render error in any widget unwinds to the app-level boundary
 * and the whole shell goes with it — which is what Dispatch → Overview did
 * when the jobs read came back in Dispatch's shape (see
 * hooks/dispatchJobsQuery.ts). Each widget already owns its loading, failed
 * and empty states for a READ; this is the fourth case, a widget that could
 * not draw what it was handed, and it must not look like any of those three.
 * In particular it must not look empty: an Action Queue that drew nothing
 * would read as "nothing needs attention".
 *
 * No retry button, for LoadFailureNotice's reason: re-rendering the same data
 * throws the same error.
 */
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { WidgetPanel } from './WidgetPanel';

interface Props {
  widgetKey: string;
  /** The layout's label for the widget, so the reader knows WHICH panel failed. */
  label: string;
  children: React.ReactNode;
}

interface State {
  failed: boolean;
}

export class WidgetErrorBoundary extends React.Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`Overview widget "${this.props.widgetKey}" failed to render:`, error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <WidgetPanel widgetKey={this.props.widgetKey} eyebrow="Overview" title={this.props.label}>
        <p
          role="alert"
          data-testid="widget-render-failed"
          className="flex items-center gap-2 px-4 py-6 text-sm text-fieldiq-muted"
        >
          <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
          This panel could not be shown. The rest of the Overview is unaffected; reload the page to try it again.
        </p>
      </WidgetPanel>
    );
  }
}
